/**
 * Payment URL builder utilities
 * Constructs URLs for payment navigation with proper parameters
 */

export interface OrderUrlParams {
	productId: number
	paymentId: string
	installmentCode: string
	serviceId?: number
}

/**
 * Builds the order URL with payment parameters
 * @param params - Parameters for URL construction
 * @returns Complete order URL with query parameters
 *
 * @example
 * buildPaymentUrl({
 *   productId: 123,
 *   paymentId: 'vsegdada',
 *   installmentCode: '0-0-12',
 *   serviceId: 456
 * })
 * // returns: '/order/123,456?payment=vsegdada&period=0-0-12'
 */
export const buildPaymentUrl = ({ productId, paymentId, installmentCode, serviceId }: OrderUrlParams): string => {
	const ids = [productId, ...(serviceId ? [serviceId] : [])].join(',')
	const searchParams = new URLSearchParams({
		payment: paymentId,
		period: installmentCode
	})
	return `/order/${ids}?${searchParams.toString()}`
}

/**
 * Builds the order URL for credit products (without installment period)
 * @param productId - Product ID
 * @param paymentId - Payment provider ID
 * @param serviceId - Optional service/related product ID
 * @returns Complete order URL for credit payment
 */
export const buildCreditUrl = (productId: number, paymentId: string, serviceId?: number): string => {
	const ids = [productId, ...(serviceId ? [serviceId] : [])].join(',')
	const searchParams = new URLSearchParams({
		payment: paymentId
	})
	return `/order/${ids}?${searchParams.toString()}`
}

/**
 * Constructs installment code from months
 * Standard format: '0-0-{months}'
 * @param months - Number of months
 * @returns Installment code string
 *
 * @example
 * buildInstallmentCode(12) // returns '0-0-12'
 * buildInstallmentCode(6) // returns '0-0-6'
 */
export const buildInstallmentCode = (months: number): string => `0-0-${months}`
